import {
  Banner,
  Button,
  Divider,
  FlexBox,
  Modal,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useCallback, useState } from 'react';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import {
  FacebookAuthenticationData,
  FacebookAuthenticationError,
  FacebookLoginButton,
  FacebookLoginButtonProps,
} from '../../../../facebookLoginButton';

import { InstagramConnectModal } from './components/instagramConnectModal/instagramConnectModal';
import styles from './instagramAuthenticator.module.scss';

type InstagramAuthenticatorProps = PropsWithChildren & {
  /** The id of the facebook app. */
  appId: string;
  /** The props of the facebook login button. */
  facebookButtonProps?: Omit<
    FacebookLoginButtonProps,
    'scope' | 'onSuccess' | 'onError'
  >;
  /** The callback for when facebook was authenticated. */
  onSuccess: (queueId: string) => void;
};

/** The authentication steps. */
const steps: Record<'text' | 'link', string>[] = [
  {
    text: 'Eligible Instagram Business account.',
    link: 'https://www.facebook.com/help/instagram/502981923235522',
  },
  {
    text: 'Instagram connected to a Facebook page.',
    link: 'https://www.facebook.com/business/help/898752960195806',
  },
  {
    text: 'Instagram messaging permissions enabled.',
    link: 'https://www.facebook.com/help/instagram/791161338412168',
  },
];

/**
 * A component that handles authenticating to Facebook and setting up the
 * Instagram channel.
 */
export const InstagramAuthenticator = ({
  appId,
  facebookButtonProps,
  children,
  onSuccess,
}: InstagramAuthenticatorProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const [prerequisitesModalOpen, setPrerequisitesModalOpen] = useState(false);
  const [authenticationError, setAuthenticationError] =
    useState<FacebookAuthenticationError>();

  /** Handles closing the modal. */
  const handleClose = useCallback(() => {
    setPrerequisitesModalOpen(false);
  }, []);

  /** Handles the facebook authentication. */
  const handleFacebookAuthenticated = async (
    facebookAuthenticationData: FacebookAuthenticationData,
  ) => {
    const { queueId } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:instagramConnect',
          arg: {
            appId,
            facebookAuthenticationData,
          },
        },
      }),
    );

    onSuccess(queueId);
    handleClose();
  };

  const handleFacebookError = useCallback(
    (facebookAuthenticationError: FacebookAuthenticationError) => {
      setAuthenticationError(facebookAuthenticationError);
    },
    [],
  );

  return (
    <>
      <Button
        onClick={() => setPrerequisitesModalOpen(true)}
        {...facebookButtonProps}
      >
        {children}
      </Button>

      <Modal
        isOpen={prerequisitesModalOpen}
        className={styles.modal}
        onClose={handleClose}
      >
        <ModalHead title="Instagram for Business" onClose={handleClose} />

        <ModalBody>
          <FlexBox gap={'300'} flexDirection="column">
            {authenticationError?.error && (
              <Banner
                variant="fail"
                description={
                  <Text.Paragraph>
                    {authenticationError.error === 'incorrect_configuration'
                      ? `The selected Facebook pages are not associated with the selected Instagram account. Please restart the configuration process and select the proper page.`
                      : `No Facebook page was selected. Please restart the configuration process and select the page connected with the desired Instagram account.`}
                  </Text.Paragraph>
                }
              />
            )}
            <Text.Paragraph>
              Instagram is one of the most popular social applications in the
              world. Instagram is a Facebook company and requires access to a
              linked Facebook account and the mobile Instagram app for channel
              creation.
            </Text.Paragraph>

            <Divider />

            <Text.Paragraph>{"What you'll need"}</Text.Paragraph>
            <ul>
              {steps.map(({ text }, index) => (
                <li key={index}>
                  <Text.Paragraph>{text}</Text.Paragraph>
                </li>
              ))}
            </ul>

            <Text.Paragraph>
              Complete instructions and walk-through can be found{' '}
              <Text.ExternalLink
                role="button"
                onClick={() => {
                  elevioClient.openArticle('725');
                }}
              >
                here.
              </Text.ExternalLink>
            </Text.Paragraph>
          </FlexBox>
        </ModalBody>

        <FlexBox justifyContent="flex-end" gap="300" padding="500">
          <Button variant="tertiary" onClick={handleClose}>
            Cancel
          </Button>

          <FacebookLoginButton
            scope="pages_show_list,instagram_basic,instagram_manage_messages,pages_manage_metadata,pages_read_engagement,pages_messaging"
            onSuccess={handleFacebookAuthenticated}
            onError={handleFacebookError}
            platform="instagram"
          >
            Continue
          </FacebookLoginButton>
        </FlexBox>
      </Modal>

      <InstagramConnectModal />
    </>
  );
};
